/**
 * UserManagementPage
 *
 * Thin orchestrator page that composes the InvitesTab, UsersTab and
 * related modals. All UI state lives in the `useUserManagementStore`
 * Zustand store; tab-specific derived data (filtering, pagination) is
 * provided by `useInviteTab` / `useUserTab` hooks that also read from
 * the store.
 *
 * ## Page Hierarchy
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/pages/UserManagementPage/`
 * - **Route**: `/user-management`
 * - **Layout**: HomeLayout
 *
 * @example <UserManagementPage />
 */
import React, { useEffect } from 'react';
import { Breadcrumb, Skeleton, Tabs, Typography } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';

import { useLanguage } from '@/shared/hooks/useLanguage';
import { useUserInfo } from '@/features/auth/hooks/useUserInfo';
import { useUserPermissions } from '@/shared/hooks/useUserPermissions';
import { useShallow } from 'zustand/react/shallow';
import { useUserManagementStore, TAB_KEYS } from '@/features/users/store';
import type { TabKey } from '@/features/users/store';
import { useInviteTab } from '@/features/users/hooks/useInviteTab';
import { useUserTab } from '@/features/users/hooks/useUserTab';

import { UserManagementActions } from '@/features/users/components/ui/molecules/UserManagementActions';
import { InvitesTab } from '@/features/users/components/ui/organisms/InvitesTab';
import { UsersTab } from '@/features/users/components/ui/organisms/UsersTab';
import { CancelInviteModal } from '@/features/users/components/ui/organisms/CancelInviteModal';
import { EditProfileModal } from '@/features/users/components/ui/organisms/EditProfileModal';
import { DeleteUserModal } from '@/features/users/components/ui/organisms/DeleteUserModal';
import { InviteUserModal } from '@/features/users/components/ui/organisms/InviteUserModal';
import { ProfilesInfoModal } from '@/features/users/components/ui/organisms/ProfilesInfoModal';
import { ResendInviteModal } from '@/features/users/components/ui/organisms/ResendInviteModal';

import './UserManagementPage.css';

export const UserManagementPage: React.FC = () => {
  const { t } = useLanguage();
  const { data: userInfo } = useUserInfo();
  const [searchParams] = useSearchParams();
  const {
    canView,
    canCreate: canInvite,
    canEdit,
    canDelete,
    isLoading: userInfoLoading,
  } = useUserPermissions();

  // ── Store ────────────────────────────────────────────────────
  const {
    activeTab,
    setActiveTab,
    inviteModalOpen,
    cancelInviteModalOpen,
    resendInviteModalOpen,
    editProfileModalOpen,
    deleteModalOpen,
    profilesInfoModalOpen,
    selectedInvite,
    selectedUser,
    openInviteModal,
    closeInviteModal,
    closeCancelInviteModal,
    closeResendInviteModal,
    openEditProfileModal,
    closeEditProfileModal,
    openDeleteModal,
    closeDeleteModal,
    openProfilesInfoModal,
    closeProfilesInfoModal,
  } = useUserManagementStore(
    useShallow((s) => ({
      activeTab: s.activeTab,
      setActiveTab: s.setActiveTab,
      inviteModalOpen: s.inviteModalOpen,
      cancelInviteModalOpen: s.cancelInviteModalOpen,
      resendInviteModalOpen: s.resendInviteModalOpen,
      editProfileModalOpen: s.editProfileModalOpen,
      deleteModalOpen: s.deleteModalOpen,
      profilesInfoModalOpen: s.profilesInfoModalOpen,
      selectedInvite: s.selectedInvite,
      selectedUser: s.selectedUser,
      openInviteModal: s.openInviteModal,
      closeInviteModal: s.closeInviteModal,
      closeCancelInviteModal: s.closeCancelInviteModal,
      closeResendInviteModal: s.closeResendInviteModal,
      openEditProfileModal: s.openEditProfileModal,
      closeEditProfileModal: s.closeEditProfileModal,
      openDeleteModal: s.openDeleteModal,
      closeDeleteModal: s.closeDeleteModal,
      openProfilesInfoModal: s.openProfilesInfoModal,
      closeProfilesInfoModal: s.closeProfilesInfoModal,
    }))
  );

  // ── Tab hooks ────────────────────────────────────────────────
  const inviteTab = useInviteTab();
  const userTab = useUserTab();

  // ── Per-tab 401 detection ────────────────────────────────────
  const invitesDisabled = inviteTab.isUnauthorized;
  const usersDisabled = userTab.usersUnauthorized;

  // ── Sync active tab from URL query param on mount ─────────────
  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabKey | null;
    if (tabParam && Object.values(TAB_KEYS).includes(tabParam)) {
      setActiveTab(tabParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-switch to the accessible tab when the active one is unauthorized
  useEffect(() => {
    if (invitesDisabled && activeTab === TAB_KEYS.INVITES && !usersDisabled) {
      setActiveTab(TAB_KEYS.USERS);
    } else if (
      usersDisabled &&
      activeTab === TAB_KEYS.USERS &&
      !invitesDisabled
    ) {
      setActiveTab(TAB_KEYS.INVITES);
    }
  }, [invitesDisabled, usersDisabled, activeTab, setActiveTab]);

  // ── Permissions ─────────────────────────────────────────────
  // Derived from useUserPermissions() above — no local useMemo needed.

  // ── Loading guard ────────────────────────────────────────────
  if (userInfoLoading) {
    return (
      <div className="user-management-page">
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  // ── Access guard ─────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="user-management-page" role="alert">
        <Typography.Title level={4} className="user-management-page__title">
          {t('User management')}
        </Typography.Title>
        <Typography.Paragraph>{t('Access not allowed')}</Typography.Paragraph>
      </div>
    );
  }

  return (
    <div className="user-management-page" aria-label={t('User management')}>
      <Breadcrumb
        className="user-management-page__breadcrumb"
        items={[
          {
            title: (
              <Link to="/">
                <HomeOutlined />
              </Link>
            ),
          },
          { title: t('User management') },
        ]}
      />

      <div className="user-management-page__header">
        <Typography.Title level={4} className="user-management-page__title">
          {t('User management')}
        </Typography.Title>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as TabKey)}
        className="user-management-page__tabs"
        items={[
          {
            key: TAB_KEYS.INVITES,
            label: t('Invites'),
            disabled: invitesDisabled,
          },
          {
            key: TAB_KEYS.USERS,
            label: t('Users'),
            disabled: usersDisabled,
          },
        ]}
      />

      <UserManagementActions
        filterBadgeCount={
          activeTab === TAB_KEYS.USERS
            ? userTab.activeFiltersCount
            : inviteTab.activeFiltersCount
        }
        filtersLabel={t('Filter')}
        filtersAriaLabel={t('Open filters')}
        inviteLabel={t('Invite user')}
        canInvite={canInvite && activeTab === TAB_KEYS.INVITES}
        onOpenFilters={
          activeTab === TAB_KEYS.USERS
            ? userTab.handleOpenFilters
            : inviteTab.handleOpenFilters
        }
        onInviteUser={openInviteModal}
        onRefreshTable={
          activeTab === TAB_KEYS.USERS
            ? userTab.handleRefresh
            : inviteTab.handleRefresh
        }
        refreshing={
          activeTab === TAB_KEYS.USERS
            ? userTab.isRefreshing
            : inviteTab.isRefreshing
        }
        refreshAriaLabel={t('Refresh table')}
        ariaLabel={t('User management actions')}
      />

      {activeTab === TAB_KEYS.INVITES &&
        (invitesDisabled ? (
          <Typography.Paragraph>{t('Access not allowed')}</Typography.Paragraph>
        ) : (
          <InvitesTab tab={inviteTab} />
        ))}

      {activeTab === TAB_KEYS.USERS &&
        (usersDisabled ? (
          <Typography.Paragraph>{t('Access not allowed')}</Typography.Paragraph>
        ) : (
          <UsersTab
            tab={userTab}
            canEdit={canEdit}
            canDelete={canDelete}
            onEditUser={openEditProfileModal}
            onDeleteUser={openDeleteModal}
          />
        ))}

      {/* ── Modals ─────────────────────────────────────────────── */}
      <CancelInviteModal
        open={cancelInviteModalOpen}
        invite={selectedInvite}
        onClose={closeCancelInviteModal}
      />

      <ResendInviteModal
        open={resendInviteModalOpen}
        invite={selectedInvite}
        onClose={closeResendInviteModal}
      />

      <EditProfileModal
        open={editProfileModalOpen}
        user={selectedUser}
        businessAccountName={userInfo?.businessAccount?.name}
        onClose={closeEditProfileModal}
        onOpenProfilesInfo={openProfilesInfoModal}
      />

      <DeleteUserModal
        open={deleteModalOpen}
        user={selectedUser}
        businessAccountName={userInfo?.businessAccount?.name}
        onClose={closeDeleteModal}
      />

      <ProfilesInfoModal
        open={profilesInfoModalOpen}
        onClose={closeProfilesInfoModal}
      />

      <InviteUserModal
        open={inviteModalOpen}
        entityName={userInfo?.businessAccount?.name}
        onClose={closeInviteModal}
        onSuccess={closeInviteModal}
      />
    </div>
  );
};

export default UserManagementPage;
